import { forwardRef, useImperativeHandle, useMemo, useRef } from 'react';
import type { MouseEvent } from 'react';
import {
  computeHeatmapColorScale,
  formatExpirationLabel,
  selectHeatmapDisplayRows,
} from '@/lib/heatmapTable.utils';
import { normalizeValue } from '@/lib/viridis';
import type { ExposureRow, ExposureType, Ticker } from '@/store/heatmapStore';
import type { HeatmapTableHandle } from '../HeatmapTable';
import { DashboardHeatmapHeader } from './DashboardHeatmapHeader';
import { DashboardHeatmapRow } from './DashboardHeatmapRow';
import { HeatmapColorScale } from './HeatmapColorScale';
import { HeatmapTooltip, type HeatmapTooltipHandle } from './HeatmapTooltip';
import type { TimeSpentCellValue, TimeSpentColumn } from './timeSpentColumn.utils';
import { useHeatmapCentering } from './useHeatmapCentering';

interface DashboardHeatmapProps {
  rows: ExposureRow[];
  expirations: string[];
  spotPrice: number;
  visibleStrikeCount?: number | null;
  type: ExposureType;
  ticker: Ticker;
  timeSpent: TimeSpentColumn;
  timeSpentLabel?: string | null;
}

export const DashboardHeatmap = forwardRef<HeatmapTableHandle, DashboardHeatmapProps>(
  function DashboardHeatmap(
    { rows, expirations, spotPrice, visibleStrikeCount, type, ticker, timeSpent, timeSpentLabel },
    ref,
  ) {
    const tableRef = useRef<HTMLTableElement>(null);
    const tooltipRef = useRef<HeatmapTooltipHandle>(null);
    const scrollerRef = useRef<HTMLDivElement>(null);
    const displayRows = useMemo(
      () => selectHeatmapDisplayRows(rows, expirations, spotPrice, ticker, visibleStrikeCount),
      [rows, expirations, spotPrice, ticker, visibleStrikeCount],
    );
    const { colorMin, colorMax, spotRowIndex, kingCell } = useMemo(
      () => computeHeatmapColorScale(displayRows, expirations, spotPrice, type, ticker),
      [displayRows, expirations, spotPrice, type, ticker],
    );
    const timeSpentByStrike = useMemo(
      () =>
        new Map(
          timeSpent.kind === 'ready'
            ? timeSpent.buckets.map((bucket) => [bucket.strike, bucket.percent])
            : [],
        ),
      [timeSpent],
    );
    const showTimeSpent = timeSpent.kind !== 'hidden';
    const timeSpentCell = (strike: number): TimeSpentCellValue | null =>
      timeSpent.kind === 'hidden'
        ? null
        : timeSpent.kind === 'ready'
          ? (timeSpentByStrike.get(strike) ?? 0)
          : timeSpent.kind;
    // Scale against the whole session, as the reference does, so the gold bar is the session's POC strike.
    const maxTimeSpentPercent = Math.max(0, ...timeSpentByStrike.values());

    const { flashStrike, centerRow } = useHeatmapCentering({ tableRef, scrollerRef, displayRows });

    useImperativeHandle(ref, () => ({
      scrollToSpot: (options) => centerRow(spotRowIndex, false, options),
      scrollToKing: (options) => centerRow(kingCell?.rowIndex ?? null, true, options),
    }));

    const handleMouseMove = (event: MouseEvent<HTMLTableElement>) => {
      const cell = (event.target as HTMLElement).closest<HTMLElement>('td[data-expiration]');
      const row = cell != null ? displayRows[Number(cell.dataset.row)] : undefined;
      const expiration = cell?.dataset.expiration;
      const exposure = row != null && expiration != null ? row.cells[expiration] : undefined;
      if (row == null || expiration == null || exposure == null) {
        tooltipRef.current?.hide();
        return;
      }
      tooltipRef.current?.show(
        {
          title: `${ticker} · ${formatExpirationLabel(expiration)}`,
          strike: row.strike.toFixed(1),
          label: `Net ${type.toUpperCase()}`,
          value: exposure.formatted,
        },
        event.clientX,
        event.clientY,
      );
    };

    if (displayRows.length === 0) {
      return (
        <div className="flex h-64 items-center justify-center rounded-md border border-line bg-panel font-mono text-xs text-dim">
          No data available
        </div>
      );
    }

    return (
      <div className="flex flex-col rounded-md border border-line bg-panel pb-2 pt-0.5 fit:min-h-0 fit:flex-1 min-[721px]:px-2 min-[721px]:pb-2.5 min-[721px]:pt-1">
        <div
          ref={scrollerRef}
          className="overflow-x-auto pb-1 fit:min-h-0 fit:flex-1 fit:overflow-auto fit:[overscroll-behavior:contain] min-[721px]:pb-0"
        >
          <table
            ref={tableRef}
            aria-label={`${type.toUpperCase()} heatmap`}
            onMouseMove={handleMouseMove}
            onMouseLeave={() => tooltipRef.current?.hide()}
            className="table-fixed border-collapse [--hm-row:30px] max-[720px]:w-max min-[721px]:w-full min-[721px]:min-w-[640px] min-[721px]:[--hm-row:31px]"
          >
            <colgroup>
              <col className="w-16 min-[721px]:w-[74px]" />
              {expirations.map((expiration) => (
                <col
                  key={expiration}
                  className="w-[74px] min-[721px]:w-auto min-[721px]:min-w-[76px]"
                />
              ))}
              {showTimeSpent && <col className="w-[104px] min-[721px]:w-[118px]" />}
            </colgroup>
            <DashboardHeatmapHeader
              expirations={expirations}
              timeSpentLabel={showTimeSpent ? (timeSpentLabel ?? '') : null}
            />
            <tbody>
              {displayRows.map((row, rowIndex) => (
                <DashboardHeatmapRow
                  key={row.strike}
                  row={row}
                  rowIndex={rowIndex}
                  expirations={expirations}
                  colorMin={colorMin}
                  colorMax={colorMax}
                  isSpotRow={rowIndex === spotRowIndex}
                  kingExpiration={kingCell?.rowIndex === rowIndex ? kingCell.expiration : null}
                  isFlashing={flashStrike === row.strike}
                  timeSpent={timeSpentCell(row.strike)}
                  maxTimeSpentPercent={maxTimeSpentPercent}
                />
              ))}
            </tbody>
          </table>
        </div>
        <HeatmapColorScale zeroPosition={normalizeValue(0, colorMin, colorMax)} />
        <HeatmapTooltip ref={tooltipRef} />
      </div>
    );
  },
);
